// lib/minuspunkte.js
//
// Minuspunkte in allen Kampfsportarten mit Punktestand (20.10.2026), Abschrift von
// backend/utils/minuspunkte.js für Schreiberansicht, Kampfrichter-App und
// Anzeigen. Gerechnet wird in der Kampfrichter-App am Server; hier steht
// dieselbe Rechnung für die Schreiberansicht im Dashboard, die ohne Server
// zählt. backend/utils/__tests__/minuspunkte.test.js vergleicht beide bei
// jedem `npm test`.
//
//   Stand = Wertungspunkte − Minuspunkte      (darf negativ werden)
//   Minuspunkte = von Hand gegebene + je einer für die 3., 4., 5. …
//                 Verwarnung (Entscheidung des Veranstalters 21.10.2026:
//                 die ersten zwei Verwarnungen sind nur Verwarnungen, ab der
//                 dritten gibt JEDE einen Minuspunkt; keine Disqualifikation
//                 über Verwarnungen, der Zähler läuft weiter)
//   Automatische Disqualifikation ab Verwarnung Nr. n nur, wenn in den
//   Wertungs-Einstellungen je Divisionstyp/Turnier eingestellt (21.10.2026,
//   Standard aus): regelFuer → autoDqAb, autoDqVermerk.
//
// ⚠️ Die Tabelle SPORTARTEN wird im Backend umgeschaltet und hierher
// abgeschrieben; der Test fällt, wenn beide auseinanderlaufen.

// Ab dieser Verwarnung gibt jede weitere einen Minuspunkt.
const VERWARNUNG_FUER_MINUS = 3;
// Verwarnungen ohne Folge (die ersten zwei)
const FREIE_VERWARNUNGEN = VERWARNUNG_FUER_MINUS - 1;

const zahl = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : 0;
};

// ─── Regeln je Sportart: HIER umschalten ───────────────────────────────────
// Dieselbe Zeile in backend/utils/minuspunkte.js und in der Abschrift
// frontend/src/lib/minuspunkte.js ändern; npm test fällt, wenn sie abweichen.
//
//   vonHand:          Der Hauptkampfrichter (Kampfrichter-App) bzw. der
//                     Schreiber darf Minuspunkte geben und zurücknehmen.
//                     Nur wo es einen Punktestand je Kämpfer gibt.
//   ausVerwarnung:    Ab der 3. Verwarnung gibt jede Verwarnung einen
//                     Minuspunkt. Seit 21.10.2026 in allen Kampfsportarten
//                     mit Punktestand (Veranstalter); 3 Verwarnungen
//                     disqualifizieren nirgends mehr. Disqualifikation gibt es
//                     nur noch als eigene Siegart (DQ/Hansoku).
//   autoDqAb:         Automatische Disqualifikation ab dieser Verwarnung
//                     (Zahl ≥ 1), null = aus. Standard der Sportart im Code;
//                     überschrieben aus den Wertungs-Einstellungen je
//                     Divisionstyp und je Turnier
//                     (wertungs_einstellungen / turnier_wertungs_einstellungen
//                     .auto_dq_ab_verwarnung, siehe autoDqWert). Entscheidung
//                     des Veranstalters 21.10.2026: Standard überall aus.
const SPORTARTEN = {
  kumite:        { vonHand: true,  ausVerwarnung: true, autoDqAb: null },
  pointfighting: { vonHand: true,  ausVerwarnung: true, autoDqAb: null },
  k1:            { vonHand: true,  ausVerwarnung: true, autoDqAb: null },
  fullcontact:   { vonHand: true,  ausVerwarnung: true, autoDqAb: null },
  continuous:    { vonHand: true,  ausVerwarnung: true, autoDqAb: null },
  // Kickboxen ohne genauere Angabe (alte Daten, nur Divisionscode)
  kickboxen:     { vonHand: true,  ausVerwarnung: true, autoDqAb: null },
  // Punkte (Kampfrichter-App: Kumite-Bedienung, Schreiber: Kickbox-Bedienung)
  grappling:     { vonHand: true,  ausVerwarnung: true, autoDqAb: null },
  // Kampfrichter-App: Punkte wie die anderen. Die Schreiberansicht zählt
  // Gürtel (0 bis 3) und zeigt dort keine Minuspunkte (siehe BracketDashboard).
  rumble:        { vonHand: true,  ausVerwarnung: true, autoDqAb: null },
  // Unbekannte Kampfdisziplin mit Punktestand: wie alle anderen
  sonstige:      { vonHand: true,  ausVerwarnung: true, autoDqAb: null },
  // Kein Kampfstand
  formen:        { vonHand: false, ausVerwarnung: false, autoDqAb: null },
  bruchtest:     { vonHand: false, ausVerwarnung: false, autoDqAb: null },
};

/**
 * Welche Sportart ist es? Gesucht wird erst im Kampfmodus der Pool-Liste
 * (brackets.modus_id: rumble, pointfighting, continuous, fullcontact, k1,
 * kumite, grappling), dann im Divisionstyp aus den Daten
 * (z. B. „Continous Fighting“, „Vollkontakt“), zuletzt in der Bedienung der
 * Kampfrichter-App (session_typ: kumite, kickboxen, kata, bruchtest).
 */
function sportartAusText(text) {
  const t = String(text || '').trim().toLowerCase();
  if (!t) return null;
  if (t.includes('kumite')) return 'kumite';
  if (t.includes('pointfight') || t.includes('point sparring') || t.includes('point fighting')) return 'pointfighting';
  if (t === 'k1' || t === 'k-1' || t.startsWith('k1 ') || t.startsWith('k-1 ')) return 'k1';
  if (t.includes('fullcontact') || t.includes('full contact') || t.includes('vollkontakt')) return 'fullcontact';
  if (t.includes('contin')) return 'continuous';
  if (t.includes('grappl')) return 'grappling';
  if (t.includes('rumble')) return 'rumble';
  if (t.includes('kickbox')) return 'kickboxen';
  if (t.includes('form') || t.includes('kata') || t.includes('selbstverteidigung')) return 'formen';
  if (t.includes('bruch')) return 'bruchtest';
  return 'sonstige';
}

function sportartVon({ sessionTyp, divisionType, modusId } = {}) {
  const ausModus = sportartAusText(modusId);
  if (ausModus && ausModus !== 'sonstige') return ausModus;
  const ausTyp = sportartAusText(divisionType);
  if (ausTyp) return ausTyp;
  const s = String(sessionTyp || 'kumite').trim().toLowerCase();
  if (s === 'kata') return 'formen';
  if (s === 'bruchtest') return 'bruchtest';
  if (s === 'kickboxen' || s === 'kickboxing') return 'kickboxen';
  return 'kumite';
}

/**
 * Wert aus den Wertungs-Einstellungen (auto_dq_ab_verwarnung) gegen den
 * Standard der Sportart: null/undefined/'' = Standard übernehmen,
 * 0 (oder ungültig) = ausdrücklich aus, Zahl ≥ 1 = ab dieser Verwarnung.
 */
function autoDqWert(standard, einstellung) {
  if (einstellung === null || einstellung === undefined || einstellung === '') {
    return standard == null ? null : autoDqWert(null, standard);
  }
  const n = Number(einstellung);
  if (!Number.isFinite(n) || n < 1) return null;
  return Math.trunc(n);
}

/**
 * Regel für einen Kampf: { sportart, vonHand, ausVerwarnung, autoDqAb }.
 * angaben.autoDqEinstellung: Wert aus den Wertungs-Einstellungen (Turnier
 * vor Standard des Divisionstyps, siehe controllers/wertungsController.js).
 * Automatische Disqualifikation nur, wo es einen Punktestand gibt (vonHand).
 */
function regelFuer(angaben = {}) {
  const sportart = sportartVon(angaben);
  const r = SPORTARTEN[sportart];
  return {
    sportart, vonHand: r.vonHand, ausVerwarnung: r.ausVerwarnung,
    autoDqAb: r.vonHand ? autoDqWert(r.autoDqAb, angaben.autoDqEinstellung) : null,
  };
}

/** Disqualifiziert diese Zahl Verwarnungen bei dieser Regel (autoDqAb)? */
function autoDisqualifiziert(verwarnungen, autoDqAb) {
  return autoDqAb != null && autoDqAb >= 1 && zahl(verwarnungen) >= autoDqAb;
}

/**
 * Automatische Disqualifikation nach einer Verwarnung bzw. deren Rücknahme.
 *   vorher: bisheriger Vermerk { farbe, verwarnungen, ab } oder null
 *   farbe:  Seite, deren Verwarnungen sich geändert haben
 *   verwarnungen: neue Zahl dieser Seite
 * Gibt den neuen Vermerk zurück (oder null). Wer zuerst die Grenze erreicht,
 * ist disqualifiziert; eine Rücknahme unter die Grenze hebt es wieder auf.
 */
function autoDqVermerk(vorher, farbe, verwarnungen, autoDqAb) {
  const erreicht = autoDisqualifiziert(verwarnungen, autoDqAb);
  if (vorher && vorher.farbe && vorher.farbe !== farbe) return vorher;
  if (erreicht) return vorher && vorher.farbe === farbe ? { ...vorher, verwarnungen: zahl(verwarnungen) } : { farbe, verwarnungen: zahl(verwarnungen), ab: autoDqAb };
  return null;
}

/** „Rot disqualifiziert · 3. Verwarnung“ */
function autoDqText(vermerk) {
  if (!vermerk || !vermerk.farbe) return '';
  return `${vermerk.farbe === 'rot' ? 'Rot' : 'Blau'} disqualifiziert · ${zahl(vermerk.verwarnungen)}. Verwarnung`;
}

// ─── Schreiberansicht (Dashboard, BracketDashboard → FightDetailView) ──────
// Der Schreiber zählt ohne Server. Seit 21.10.2026 (Veranstalter) gilt in
// BEIDEN Bedienungen, Kickboxen und Kumite, dieselbe Regel wie in der
// Kampfrichter-App: eine Verwarnungszählung je Kämpfer, die ersten zwei nur
// Verwarnung, ab der 3. je ein Minuspunkt, keine Gegnerpunkte.
// Disqualifikation nur über autoDqAb (Einstellung, Standard aus) oder den
// Knopf „disqualifizieren“. Die früheren Kumite-Strafen C1 (Kontakt) und
// C2 (Verhalten) mit Hansoku bei 4 gibt es in der Bedienung nicht mehr.
// Ausnahme: Rumble in der Kickbox-Bedienung zählt Gürtel (0 bis 3); dort
// keine Minuspunkte und keine automatische Disqualifikation.

/**
 * Regel für die Schreiberansicht:
 *   { ...regelFuer, guertel, vonHand, ausVerwarnung, autoDqAb }
 *   scoringSystem: 'kickboxing' | 'kumite' (Bedienung im Schreiber)
 */
function schreiberRegel({ scoringSystem, modusId, divisionType, autoDqEinstellung } = {}) {
  const kumite = scoringSystem === 'kumite';
  const regel = regelFuer({ modusId, divisionType, sessionTyp: kumite ? 'kumite' : 'kickboxen', autoDqEinstellung });
  const guertel = !kumite && String(modusId || '').trim().toLowerCase() === 'rumble';
  return {
    ...regel,
    guertel,
    vonHand: !guertel && regel.vonHand,
    ausVerwarnung: !guertel && regel.ausVerwarnung,
    autoDqAb: guertel ? null : regel.autoDqAb,
  };
}

/**
 * Stand nach dem Setzen der Verwarnungen von `alt` auf `neu` (Schreiber):
 * die automatischen Minuspunkte (ab der 3.) kommen dazu bzw. gehen mit der
 * Rücknahme. Von Hand gegebene Minuspunkte und Wertungspunkte bleiben.
 */
function standNachVerwarnungen(stand, alt, neu, gelten) {
  const a = Math.max(0, zahl(alt));
  const n = Math.max(0, zahl(neu));
  return zahl(stand) - (automatischeMinus(n, gelten) - automatischeMinus(a, gelten));
}

/** Schreiber: welche Seite ist automatisch disqualifiziert? 1, 2 oder 0. */
function autoDqSeite(verwarnungen1, verwarnungen2, autoDqAb) {
  if (autoDisqualifiziert(verwarnungen1, autoDqAb)) return 1;
  if (autoDisqualifiziert(verwarnungen2, autoDqAb)) return 2;
  return 0;
}

/** Geben Verwarnungen ab der 3. hier Minuspunkte? (Kampfrichter-App) */
function minusAusVerwarnung(sessionTyp, divisionType) {
  return regelFuer({ sessionTyp, divisionType }).ausVerwarnung;
}

/** Darf hier von Hand ein Minuspunkt gegeben werden? (Kampfrichter-App) */
function minusVonHand(sessionTyp, divisionType) {
  return regelFuer({ sessionTyp, divisionType }).vonHand;
}

/** Minuspunkte aus Verwarnungen: 0 für die ersten zwei, danach je einer. */
function automatischeMinus(verwarnungen, gelten) {
  return gelten ? Math.max(0, zahl(verwarnungen) - FREIE_VERWARNUNGEN) : 0;
}

/**
 * Kurztext zu den Verwarnungen für Bedienung und Anzeigen:
 *   0 → '', 1 → 'Verwarnung 1', 2 → 'Verwarnung 2',
 *   3 → 'Verwarnung 3 · −1', 4 → 'Verwarnung 4 · −2' (mit gelten)
 */
function verwarnungText(verwarnungen, gelten = true) {
  const n = Math.max(0, zahl(verwarnungen));
  if (!n) return '';
  const m = automatischeMinus(n, gelten);
  return m > 0 ? `Verwarnung ${n} · −${m}` : `Verwarnung ${n}`;
}

function ereignisAnwenden(seite, art, { punkte = 1, gelten = true } = {}) {
  let wertung = Math.max(0, zahl(seite.wertung ?? (zahl(seite.punkte) + zahl(seite.minus))));
  let verwarnungen = Math.max(0, zahl(seite.verwarnungen));
  let hand = Math.max(0, zahl(seite.minus_hand));
  switch (art) {
    case 'punkt': wertung += Math.max(0, zahl(punkte)); break;
    case 'undo-punkt': wertung = Math.max(0, wertung - 1); break;
    case 'verwarnung': verwarnungen += 1; break;
    case 'undo-verwarnung': verwarnungen = Math.max(0, verwarnungen - 1); break;
    case 'minus': hand += 1; break;
    case 'undo-minus': hand = Math.max(0, hand - 1); break;
    default: break;
  }
  const minus = hand + automatischeMinus(verwarnungen, gelten);
  return { punkte: wertung - minus, verwarnungen, minus, minus_hand: hand, wertung };
}

/** Stand als Text, negativ mit echtem Minuszeichen („−1“). */
function standText(n) {
  const z = zahl(n);
  return z < 0 ? `−${Math.abs(z)}` : String(z);
}

module.exports = {
  VERWARNUNG_FUER_MINUS, SPORTARTEN, sportartVon, regelFuer, minusAusVerwarnung, minusVonHand,
  automatischeMinus, verwarnungText, ereignisAnwenden, standText,
  autoDqWert, autoDisqualifiziert, autoDqVermerk, autoDqText,
  schreiberRegel, standNachVerwarnungen, autoDqSeite,
};
